import { PageOptions } from '@graphcommerce/framer-next-pages'
import { Asset } from '@graphcommerce/graphcms-ui'
import { flushMeasurePerf } from '@graphcommerce/graphql'
import {
  CategoryDescription,
  CategoryHeroNavTitle,
  CategoryMeta,
  getCategoryStaticPaths
} from '@graphcommerce/magento-category'
import {
  CategoryBreadcrumb
} from '../../components/CategoryBreadcrumbs/CategoryBreadcrumb'
import {
  extractUrlQuery,
  FilterTypes,
  getFilterTypes,
  parseParams,
  ProductFiltersDocument,
  ProductFiltersPro,
  ProductFiltersQuery,
  ProductListDocument,
  ProductListParams,
  ProductListQuery,
} from '@graphcommerce/magento-product'
import {
  ProductFiltersProAllFiltersChip
} from '../../components/PriceFiltersPro'
import {
  ProductListCount
} from '../../components/ProductListCount/ProductListCount'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import {
  LayoutTitle,
  LayoutHeader,
  GetStaticProps,
  MetaRobots, IconSvg, Button
} from '@graphcommerce/next-ui'
import iconArrowDown from '@graphcommerce/next-ui/icons/arrow-down.svg'
import Container from '@mui/material/Container'
import { GetStaticPaths } from 'next'
import {
  LayoutNavigation,
  LayoutNavigationProps,
  ProductListItems,
  RowProduct,
  RowRenderer,
} from '../../components'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { CategoryPageDocument, CategoryPageQuery } from '../../graphql/CategoryPage.gql'
import { graphqlSsrClient, graphqlSharedClient } from '../../lib/graphql/graphqlSsrClient'
import router from 'next/router'
import {useEffect, useState} from 'react'
import { useInView } from  "react-intersection-observer";
import {Box, CircularProgress} from '@mui/material'
import {LayoutCategoryControl} from '../../components/Layout/LayoutCategoryControl'
import {SubCategoryNavigation} from '../../components/Navigation/SubCategoryNavigation'
import {ProductFiltersPerCategoryDocument} from '../../components/ProductFiltersPro/ProductFiltersPerCategory.gql'
import {Trans} from '@lingui/react'
import {BrandsHeroNav} from '../../components/BrandsHeroNav/BrandsHeroNav'

export type CategoryProps = CategoryPageQuery &
  ProductListQuery &
  ProductFiltersQuery & { filterTypes?: FilterTypes; params?: ProductListParams, parentCategories?: any }
export type CategoryRoute = { url: string[] }

type GetPageStaticPaths = GetStaticPaths<CategoryRoute>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, CategoryProps, CategoryRoute>

const getProducts = async (params: ProductListParams) => {
  const { locale } = router
  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const pageSize = (await conf).data.storeConfig?.grid_per_page  ?? params.pageSize
  params.pageSize = pageSize

  const products = await client.query({
    query: ProductListDocument,
    variables: {
      ...params,
      sort: {
        news_from_date: 'DESC'
      }
    },
  })
  return { ...(products).data }
}

function CategoryPage(props: CategoryProps) {
  const {
    categories,
    products,
    filters,
    params,
    filterTypes,
    pages,
    parentCategories } = props

  const category = categories?.items?.[0]
  const parentCategory = parentCategories?.items?.[0]
  const isLanding = category?.display_mode === 'PAGE'
  const page = pages?.[0]
  const isCategory = params && category && products?.items && filterTypes

  const [ref, inView] = useInView();
  const [items, setItems] = useState(products?.items)
  const [currentPage, setCurrentPage] = useState(1)
  const [isFetchingPage, setIsFetchingPage] = useState(false)
  const totalPages = products?.page_info?.total_pages ?? 1

  useEffect(() => {
    if (!products) {
      return
    }
    const resetPage = 1
    setCurrentPage(resetPage)
    setIsFetchingPage(false)
    setItems(products.items)
  }, [products])

  const loadMoreItems = () => {
    if (isFetchingPage || !inView || !params || currentPage > totalPages) {
      return
    }

    setIsFetchingPage(true)
    const {
      url,
      sort,
      filters: paramsFilters,
      search,
    }: ProductListParams = params
    const nextPage = currentPage + 1
    const newParams = { filters: paramsFilters, sort, url, currentPage: nextPage, pageSize: 12, search } as ProductListParams
    getProducts(newParams).then((data) => {
      setItems(items?.concat(data?.products?.items as Array<any>))
      setCurrentPage(nextPage)
    }).catch((error) => {
      console.log("Error", error)
    }).finally(() => {
      setIsFetchingPage(false)
    })
  }

  useEffect(() => {
    loadMoreItems()
  }, [inView])

  return (
    <>
      <CategoryMeta
        params={params}
        title={page?.metaTitle}
        metaDescription={page?.metaDescription}
        metaRobots={page?.metaRobots.toLowerCase().split('_') as MetaRobots[]}
        canonical={page?.url ? `/${page.url}` : undefined}
        {...category}
      />
      <LayoutHeader floatingMd>
        <LayoutTitle size='small' component='span'>
          {category?.name ?? page.title}
        </LayoutTitle>
      </LayoutHeader>
      {isCategory && (
        <LayoutCategoryControl>
          <SubCategoryNavigation parentCategory={parentCategory} category={category} params={params} sx={(theme) => ({
            marginLeft: '0',
            [theme.breakpoints.up('md')]: {
              marginLeft: '-50px',
            },
          })} />
          { !isLanding && (
            <Box sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'end',
              marginLeft: 'auto',
            })}>
              {(category.is_anchor === 1 &&
                  (
                    (category.level && category.level > 2) || (!category.children || category.children.length === 0)
                  )) &&
                  <ProductFiltersPro params={params}>
                      <ProductFiltersProAllFiltersChip
                        {...filters}
                        appliedAggregations={products.aggregations}
                        filterTypes={filterTypes}
                      />
                  </ProductFiltersPro>
              }
              <ProductListCount total_count={products?.total_count} sx={(theme) => ({
                display: { xs: 'none', md: 'block' },
              })} />
            </Box>
          )}
          <CategoryBreadcrumb breadcrumbs={category?.breadcrumbs} uid={category.uid} name={category.name}
              sx={(theme) => ({
                display: 'none',
                backgroundColor: theme.palette.background.paper,
                [theme.breakpoints.up('md')]: {
                  display: 'flex',
                  justifyContent: 'flex-end',
                },
                paddingLeft: theme.spacings.md,
                paddingRight: theme.spacings.md,
                paddingTop: '5px',
                paddingBottom: '5px',
                position: 'absolute',
                zIndex: 1,
                bottom: '-41px',
                right: '0',
                width: '100vw',
              })}
          />
        </LayoutCategoryControl>
      )}
      {isCategory && isLanding && (
        <BrandsHeroNav
          {...category}
          asset={pages?.[0]?.asset && <Asset asset={pages[0].asset} loading='eager' />}
          title={<CategoryHeroNavTitle>{category?.name}</CategoryHeroNavTitle>}
        />
      )}
      {isCategory && !isLanding && (
        <>
          <CategoryDescription description={category.description} />
          <Container maxWidth={false}>
            <ProductListItems
              title={category.name ?? ''}
              items={items}
              loadingEager={1}
            />
            <Box
              ref={ref}
              sx={(theme) => ({
                margin: '0 auto',
                marginTop: theme.spacings.lg,
                marginBottom: theme.spacings.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              })}
            >
              { isFetchingPage && <CircularProgress color='inherit' size={20} /> }
              { (!isFetchingPage && currentPage < totalPages) &&
                <Button onClick={() => loadMoreItems()} sx={{ display: 'flex', gap: '1rem'}}>
                    <Trans id='Load more items' />
                    <IconSvg src={iconArrowDown} size='large' />
                </Button>
              }
            </Box>
          </Container>
        </>
      )}
      {page && (
        <RowRenderer
          content={page.content}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...products?.items?.[0]}
                items={products?.items?.slice(0, 8)}
              />
            ),
          }}
        />
      )}
    </>
  )
}

const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}
CategoryPage.pageOptions = pageOptions

export default CategoryPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  // Disable getStaticPaths while in development mode
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const path = (loc: string) => getCategoryStaticPaths(graphqlSsrClient(loc), loc)
  const paths = (await Promise.all(locales.map(path))).flat(1)
  const brandPaths = paths.filter((p) => {
    if (typeof p === 'string') {
      return p.startsWith('/marcas') || p.startsWith('/brands')
    }
    return p.params?.url?.[0] === 'marcas' || p.params?.url?.[0] === 'brands'
  })
  return { paths: brandPaths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const [url, query] = extractUrlQuery(params)
  if (!url || !query) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const filterTypes = getFilterTypes(client)

  const staticClient = graphqlSsrClient(locale)

  const categoryPage = staticClient.query({
    query: CategoryPageDocument,
    variables: { url },
  })
  const layout = await staticClient.query({ query: LayoutDocument })
  const productListParams = parseParams(url, query, await filterTypes)
  const filteredCategoryUid = productListParams && productListParams.filters.category_uid?.in?.[0]

  const category = await categoryPage
  let categoryUid = filteredCategoryUid
  if (!categoryUid) {
    categoryUid = category.data.categories?.items?.[0]?.uid ?? ''
    if (productListParams) productListParams.filters.category_uid = { in: [categoryUid] }
  }

  const categoryPath = category.data.categories?.items?.[0]?.url_path
  const categoryPathArr = categoryPath?.split('/')
  const prentCategoryPath = categoryPathArr?.slice(0, (categoryPathArr?.length ?? 0) - 1).join('/')
  const parentCategories = staticClient.query({
    query: CategoryPageDocument,
    variables: { url: prentCategoryPath ?? '/' }
  })

  const hasPage = filteredCategoryUid ? false : category.data.pages.length > 0
  const hasCategory = Boolean(productListParams && categoryUid)

  if (!productListParams || !(hasPage || hasCategory))
    return redirectOrNotFound(staticClient, conf, params, locale)

  // TODO: cleaner way to do this
  const isLanding = category?.data.categories?.items?.[0]?.display_mode === 'PAGE'
  if (!isLanding && layout.data?.menu?.items) {
    const brandMenuItems = layout.data.menu.items[0]?.children?.filter((item) => item?.url_path === 'marcas' || item?.url_path === 'brands')
    if (brandMenuItems && brandMenuItems[0]) {
      const updatedBrandMenuItem = {
        ...brandMenuItems[0],
        include_in_menu: 1
      };
      const updatedMenuItems = [
        ...layout.data.menu.items
      ];

      updatedMenuItems[0] = updatedBrandMenuItem;

      const updatedMenu = {
        ...layout.data.menu,
        items: updatedMenuItems
      };

      layout.data = {
        ...layout.data,
        menu: updatedMenu
      };

      if (layout.data?.menu?.items) {
        const reUpdatedMenuItems = layout.data.menu.items.map(item => {
          if (item?.children) {
            const updatedChildren = item.children.map(child => {
              return {
                ...child
              };
            });

            return {
              ...item,
              children: updatedChildren
            };
          }
          return item;
        });

        const updatedMenuModified = {
          ...layout.data.menu,
          items: reUpdatedMenuItems
        };

        layout.data = {
          ...layout.data,
          // @ts-ignore
          menu: updatedMenuModified
        };
      }
    }
  }

  if (!hasCategory) {
    return {
      props: {
        ...category.data,
        ...layout.data,
        apolloState: await conf.then(() => client.cache.extract()),
      },
      revalidate: 60 * 20,
    }
  }

  const filters = (await staticClient.query({
    query: ProductFiltersDocument,
    variables: {
      filters: {
        category_uid: { eq: categoryUid }
      }
    },
  })).data
  let filterPerCategory: ProductFiltersQuery|null = null

  // Apply filter per category
  if (filters && filters.filters?.aggregations) {
    const filtersPerCategoryData = (await staticClient.query({
      query: ProductFiltersPerCategoryDocument,
      variables: { uid: categoryUid },
    })).data

    if (filtersPerCategoryData?.categoryFilters && filtersPerCategoryData?.categoryFilters?.length > 0) {
      const setOfAttributes = new Set(filtersPerCategoryData?.categoryFilters?.map(item => item?.filter).filter(item => item))
      const categoryFilters = filters?.filters?.aggregations?.filter(
        item => setOfAttributes.has(item?.attribute_code),
      )
      const filtersAttributeCodes = Array.from(setOfAttributes)
      categoryFilters?.sort((a, b) => filtersAttributeCodes.indexOf(a?.attribute_code) - filtersAttributeCodes.indexOf(b?.attribute_code))
      filterPerCategory = { ...filters, filters: { ...filters.filters, aggregations: categoryFilters } }
    }
  }

  const products = staticClient.query({
    query: ProductListDocument,
    variables: {
      pageSize: (await conf).data.storeConfig?.grid_per_page ?? 24,
      ...productListParams,
      filters: { ...productListParams.filters, category_uid: { eq: categoryUid } },
      sort: {
        news_from_date: 'DESC'
      }
    },
  })

  if ((await products).errors) return { notFound: true }

  const { category_name, category_url_path } =
    category.data.categories?.items?.[0]?.breadcrumbs?.[0] ?? {}

  const up =
    category_url_path && category_name
      ? { href: `/${category_url_path}`, title: category_name }
      : { href: `/`, title: 'Home' }

  const result = {
    props: {
      ...category.data,
      ...(await products).data,
      ...(filterPerCategory || filters),
      ...layout.data,
      filterTypes: await filterTypes,
      params: productListParams,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
      parentCategories: {...(await parentCategories).data.categories},
    },
    revalidate: 60 * 60 * 12, // 12 hours
  }
  flushMeasurePerf()
  return result
}
